import ServiceWrapper from "Services/ServiceWrapper";
import AuthManager from "Common/AuthManager";
import gmsConfig from "Settings/AppSettings";
import {
  Commend3ApiResponse,
  Commend3CreateModelPayload,
  Commend3ListParams,
  Commend3ListResult,
  Commend3Model,
  Commend3PreviewProduct,
  Commend3Strategy,
} from "../Types";

const API_END_URL = gmsConfig.GMS_BACKEND_API_URL;
const V3_END_POINT = "recommender/v3";

export const getCommendModels = async (
  channelId: string,
  params: Commend3ListParams
): Promise<Commend3ApiResponse<Commend3ListResult>> => {
  try {
    const query = new URLSearchParams({
      page: String(params.page),
      limit: String(params.limit),
      sort_by: params.sort_by,
      sort_order: params.sort_order,
    });
    if (params.search) {
      query.append("search", params.search);
    }
    const response = await ServiceWrapper.doGet(
      `${API_END_URL}${V3_END_POINT}/${channelId}/models?${query.toString()}`,
      null,
      null,
      AuthManager.getGmsToken()
    );
    return response?.data;
  } catch (error) {
    console.error("Error fetching Commend 3.0 models", error);
    throw error;
  }
};

export const renameCommendModel = async (
  model: Commend3Model,
  name: string
): Promise<Commend3ApiResponse<Commend3Model>> => {
  try {
    const response = await ServiceWrapper.doPut(
      `${API_END_URL}${V3_END_POINT}/model/update`,
      {
        uid: model.uid,
        channel_id: model.channel_id,
        name,
        type: model.type,
        sub_models: model.sub_models ?? [],
      },
      null,
      AuthManager.getGmsToken()
    );
    return response?.data;
  } catch (error) {
    console.error("Error renaming Commend 3.0 model", error);
    throw error;
  }
};

export const cloneCommendModel = async (
  channelId: string,
  modelId: string
): Promise<Commend3ApiResponse<Commend3Model>> => {
  try {
    const response = await ServiceWrapper.doPost(
      `${API_END_URL}${V3_END_POINT}/model/clone`,
      { channel_id: channelId, model_id: modelId },
      null,
      AuthManager.getGmsToken()
    );
    return response?.data;
  } catch (error) {
    console.error("Error cloning Commend 3.0 model", error);
    throw error;
  }
};

export const deleteCommendModel = async (
  channelId: string,
  modelId: string
): Promise<Commend3ApiResponse<null>> => {
  try {
    const response = await ServiceWrapper.doDelete(
      `${API_END_URL}${V3_END_POINT}/${channelId}/model/delete/${modelId}`,
      null,
      null,
      AuthManager.getGmsToken()
    );
    return response?.data;
  } catch (error) {
    console.error("Error deleting Commend 3.0 model", error);
    throw error;
  }
};

export const getCommendStrategies = async (
  channelId: string
): Promise<Commend3ApiResponse<Commend3Strategy[]>> => {
  try {
    const response = await ServiceWrapper.doGet(
      `${API_END_URL}${V3_END_POINT}/${channelId}/strategies`,
      null,
      null,
      AuthManager.getGmsToken()
    );
    return response?.data;
  } catch (error) {
    console.error("Error fetching Commend 3.0 strategies", error);
    throw error;
  }
};

export const previewStrategy = async (
  channelId: string,
  strategyId: string,
  search: string
): Promise<Commend3ApiResponse<Commend3PreviewProduct | null>> => {
  try {
    const query = new URLSearchParams({ search });
    const response = await ServiceWrapper.doGet(
      `${API_END_URL}${V3_END_POINT}/${channelId}/strategies/${strategyId}/preview?${query.toString()}`,
      null,
      null,
      AuthManager.getGmsToken()
    );
    return response?.data;
  } catch (error) {
    console.error("Error previewing Commend 3.0 strategy", error);
    throw error;
  }
};

export const createCommendModel = async (
  payload: Commend3CreateModelPayload
): Promise<Commend3ApiResponse<Commend3Model>> => {
  try {
    const response = await ServiceWrapper.doPost(
      `${API_END_URL}${V3_END_POINT}/model/create`,
      payload,
      null,
      AuthManager.getGmsToken()
    );
    return response?.data;
  } catch (error) {
    console.error("Error creating Commend 3.0 model", error);
    throw error;
  }
};

export const getCommendModel = async (
  channelId: string,
  modelId: string
): Promise<Commend3ApiResponse<Commend3Model>> => {
  try {
    const response = await ServiceWrapper.doPost(
      `${API_END_URL}${V3_END_POINT}/${channelId}/model/${modelId}`,
      {},
      null,
      AuthManager.getGmsToken()
    );
    return response?.data;
  } catch (error) {
    console.error("Error fetching Commend 3.0 model", error);
    throw error;
  }
};

export const updateCommendModel = async (
  model: Commend3Model
): Promise<Commend3ApiResponse<Commend3Model>> => {
  try {
    const response = await ServiceWrapper.doPut(
      `${API_END_URL}${V3_END_POINT}/model/update`,
      {
        uid: model.uid,
        channel_id: model.channel_id,
        name: model.name,
        type: model.type,
        sub_models: model.sub_models ?? [],
      },
      null,
      AuthManager.getGmsToken()
    );
    return response?.data;
  } catch (error) {
    console.error("Error updating Commend 3.0 model", error);
    throw error;
  }
};
